import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthContextType, User, LoginRequest } from '../types';
import apiService from '../services/api';
import toast from 'react-hot-toast';

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    console.log('AuthContext: useEffect triggered, checking for existing session');
    // Check for existing token on mount
    const savedToken = localStorage.getItem('adminToken');
    const savedUser = localStorage.getItem('adminUser');

    console.log('AuthContext: savedToken exists:', !!savedToken);
    console.log('AuthContext: savedUser exists:', !!savedUser);

    if (savedToken && savedUser) {
      const parsedUser = JSON.parse(savedUser);
      console.log('AuthContext: Parsed saved user:', parsedUser);
      
      setToken(savedToken);
      setUser(parsedUser);
      
      // For dealer users, let's skip the token verification temporarily to see if that's the issue
      if (parsedUser.userType === 'dealer') {
        console.log('AuthContext: Dealer user detected, skipping token verification for now');
        setIsLoading(false);
        return;
      }
      
      // Verify token is still valid for admin users
      console.log('AuthContext: Verifying token with getCurrentUser...');
      apiService.getCurrentUser()
        .then((response) => {
          console.log('AuthContext: getCurrentUser response:', response);
          if (response.success && response.data) {
            console.log('AuthContext: Setting user from API:', response.data);
            setUser(response.data);
          } else {
            console.log('AuthContext: Invalid token response, clearing storage');
            // Token is invalid, clear storage
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminUser');
            setToken(null);
            setUser(null);
          }
        })
        .catch((error) => {
          console.log('AuthContext: getCurrentUser error:', error);
          // Token is invalid, clear storage
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminUser');
          setToken(null);
          setUser(null);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      console.log('AuthContext: No saved session found');
      setIsLoading(false);
    }
  }, []);

  const login = async (credentials: LoginRequest): Promise<void> => {
    setIsLoading(true);
    try {
      console.log('AuthContext: Attempting login...');
      const response = await apiService.login(credentials);
      console.log('AuthContext: Login response:', response);
      
      if (response.success && response.data) {
        const { token: newToken, user: newUser } = response.data;
        console.log('AuthContext: Login successful, user:', newUser);
        
        setToken(newToken);
        setUser(newUser);
        
        // Save to localStorage
        localStorage.setItem('adminToken', newToken);
        localStorage.setItem('adminUser', JSON.stringify(newUser));
        console.log('AuthContext: Saved to localStorage');
        
        toast.success('Login successful!');
      } else {
        throw new Error(response.error || 'Login failed');
      }
    } catch (error) {
      console.log('AuthContext: Login error:', error);
      const message = error instanceof Error ? error.message : 'Login failed';
      toast.error(message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    setIsLoading(true);
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear local state and storage
      setToken(null);
      setUser(null);
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      setIsLoading(false);
      toast.success('Logged out successfully');
    }
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    isLoading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};